// src/lib/menage-suivi-pilotage.js
//
// REFONTE « Pilotage de l'équipe » (maquette validée Dimitri 2026-08-10) —
// helpers PURS de l'écran orchestrateur. Aucun accès réseau, aucun DOM, aucune
// écriture. Le rendu vit dans MenageSuiviView.jsx ; les RÈGLES vivent ici,
// parce qu'une règle qui n'existe que dans du JSX ne peut pas être mesurée.
//
// Ce que ce module tranche, et pourquoi :
//   · la navigation par jour (l'écran n'est plus cloué sur aujourd'hui) ;
//   · le BILAN DE LA VEILLE — la seule touche de vert de la page ;
//   · l'aperçu du LENDEMAIN ;
//   · l'appariement d'une DEMANDE D'HORAIRE avec le ménage qu'elle percute ;
//   · le REPLI des cartes au-delà d'un seuil (cf. repartirCartes).

const PARIS_TZ = 'Europe/Paris'

const COMPLETED = 'completed'
const SKIPPED = 'skipped'

/** Décale une date ISO "YYYY-MM-DD" de `delta` jours. Midi pour éviter tout bord DST. */
export function decalerJour(iso, delta) {
  const d = new Date(String(iso) + 'T12:00:00')
  if (Number.isNaN(d.getTime())) return null
  d.setDate(d.getDate() + Number(delta || 0))
  return new Intl.DateTimeFormat('en-CA', { timeZone: PARIS_TZ }).format(d)
}

/**
 * « Jeu 6 août » — libellé court de la barre de navigation.
 *
 * Court par nécessité : il vit dans un bandeau à côté de deux flèches, et un
 * « Jeudi 6 août 2026 » y passerait à la ligne sur mobile.
 */
export function jourLabelCourt(iso) {
  // RÈGLE DES DATES (Dimitri, appliquée au Pilotage 2026-08-22) : « 22 août »
  // — ni jour de semaine, ni année, partout pareil.
  const d = new Date(String(iso) + 'T12:00:00')
  if (Number.isNaN(d.getTime())) return ''
  return new Intl.DateTimeFormat('fr-FR', { day: 'numeric', month: 'long', timeZone: PARIS_TZ }).format(d)
}

/** « aujourd'hui » / « hier » / « demain » / null — pour situer un jour choisi. */
export function reperJour(iso, todayIso) {
  if (!iso || !todayIso) return null
  if (iso === todayIso) return "aujourd'hui"
  if (iso === decalerJour(todayIso, -1)) return 'hier'
  if (iso === decalerJour(todayIso, 1)) return 'demain'
  return null
}

/** Runs d'un jour donné, `skipped` exclu (run annulé — il n'est ni fait, ni à faire). */
export function runsDuJour(runs, iso) {
  const list = Array.isArray(runs) ? runs : []
  return list.filter((r) => r?.scheduled_date === iso && r?.status !== SKIPPED)
}

/**
 * BILAN DE LA VEILLE — la ligne tout en haut de la colonne principale.
 *
 * C'est le SEUL endroit vert de l'écran, et seulement quand il n'y a rien à
 * faire. Le vert ne récompense pas : il dit « passe ton chemin ». Dès qu'un
 * ménage de la veille n'est pas validé, la ligne bascule en rosé — pas parce
 * que c'est grave, mais parce que ça demande un regard.
 *
 * ⚠️ NE BILANTER QU'UN JOUR RÉVOLU. `todayIso` n'est pas décoratif : sans lui,
 * naviguer vers demain faisait bilanter AUJOURD'HUI et l'écran annonçait
 * « 5 ménages non validés » à midi, pour des ménages simplement pas encore
 * faits. Un bilan porte sur ce qui est terminé, jamais sur ce qui est en cours.
 *
 * @returns {{ nb: number, nonValides: number, tousValides: boolean, texte: string, affichable: boolean }}
 *          `affichable === false` ⇒ la ligne ne s'affiche pas.
 */
export function bilanVeille(runs, veilleIso, todayIso) {
  const revolu = todayIso == null ? true : String(veilleIso) < String(todayIso)
  const jourRuns = revolu ? runsDuJour(runs, veilleIso) : []
  const nb = jourRuns.length
  const nonValides = jourRuns.filter((r) => r?.status !== COMPLETED).length
  const tousValides = nb > 0 && nonValides === 0
  // « Hier » n'est vrai que le lendemain. Sur un jour plus ancien, le mot
  // mentirait — « La veille » reste juste quel que soit le jour regardé.
  const quand = todayIso != null && veilleIso === decalerJour(todayIso, -1) ? 'Hier' : 'La veille'
  let texte = ''
  if (nb > 0) {
    texte = tousValides
      ? `${quand} · ${nb} ménage${nb > 1 ? 's' : ''}, tous validés`
      : `${quand} · ${nonValides} ménage${nonValides > 1 ? 's' : ''} non validé${nonValides > 1 ? 's' : ''}`
  }
  return { nb, nonValides, tousValides, texte, affichable: revolu && nb > 0 }
}

/**
 * APERÇU DU LENDEMAIN — la bande cliquable en bas de la colonne principale.
 *
 * Elle ne porte QUE deux nombres : combien de ménages, et combien n'ont pas
 * encore d'agent. Le second est le seul qui appelle une action ce soir.
 */
export function apercuLendemain(runs, demainIso) {
  const jour = runsDuJour(runs, demainIso)
  const nb = jour.length
  const sansAgent = jour.filter((r) => !r?.assigned_provider_email).length
  return { nb, sansAgent }
}

/**
 * Index des demandes d'horaires EN ATTENTE, par (logement, jour).
 *
 * Clé composée et non `logement_id` seul : une même chambre peut porter une
 * demande le jeudi et une autre le samedi ; indexer sur le logement ferait
 * remonter la mauvaise sur la mauvaise carte — un bandeau qui ment sur le jour
 * est pire que pas de bandeau.
 *
 * Quand DEUX demandes tombent sur le même couple (départ tardif du partant ET
 * arrivée anticipée du suivant), on garde la PREMIÈRE et on expose `autres`,
 * pour que la carte puisse dire qu'il y en a plus d'une sans en cacher une.
 */
export function indexerDemandesHoraires(pending) {
  const list = Array.isArray(pending) ? pending : []
  const idx = new Map()
  for (const d of list) {
    const lid = d?.logement_id
    const date = d?.event_date
    if (!lid || !date) continue
    const cle = `${lid}:${date}`
    const existant = idx.get(cle)
    if (existant) existant.autres += 1
    else idx.set(cle, { demande: d, autres: 0 })
  }
  return idx
}

/** Libellé d'une demande d'horaire, du point de vue du ménage qu'elle décale. */
export function libelleDemandeHoraire(demande) {
  if (!demande) return ''
  const heure = demande.requested_time || ''
  const quoi = demande.event_kind === 'early_checkin' ? 'Arrivée anticipée demandée' : 'Départ tardif demandé'
  return heure ? `${quoi} — ${heure}` : quoi
}

/**
 * ANCIENNETÉ LISIBLE d'une demande : « 4 h », « 25 min », « 2 j ».
 *
 * Arrondi À LA BAISSE, volontairement : annoncer « depuis 5 h » quand il s'est
 * écoulé 4 h 40 gonfle l'urgence d'une demande. Sous la minute → « à l'instant ».
 */
export function ancienneteLisible(isoDate, maintenant) {
  const t = Date.parse(isoDate)
  const now = maintenant instanceof Date ? maintenant.getTime() : Date.parse(maintenant)
  if (!Number.isFinite(t) || !Number.isFinite(now)) return null
  const minutes = Math.floor((now - t) / 60000)
  if (minutes < 0) return null
  if (minutes < 1) return "à l'instant"
  if (minutes < 60) return `${minutes} min`
  const heures = Math.floor(minutes / 60)
  if (heures < 24) return `${heures} h`
  const jours = Math.floor(heures / 24)
  return `${jours} j`
}

/** La demande la plus ANCIENNE d'une liste (celle qui fait attendre le plus). */
export function demandeLaPlusAncienne(pending) {
  const list = Array.isArray(pending) ? pending : []
  let best = null
  let bestT = Infinity
  for (const d of list) {
    const t = Date.parse(d?.created_at)
    if (!Number.isFinite(t)) continue
    if (t < bestT) { bestT = t; best = d }
  }
  return best
}

/** Seuil de dépliement par défaut (décision Dimitri : « au-delà de 4 »). */
export const SEUIL_CARTES_DEPLOYEES = 4

/**
 * RÉPARTITION DES CARTES DU JOUR au-delà du seuil — réponse à « prévois le
 * comportement au-delà de 4 ménages ».
 *
 * LE PRINCIPE : ce qui appelle une DÉCISION reste toujours déplié, où qu'il
 * soit dans la liste. Un ménage sans agent, ou dont le check-in est dépassé
 * sans validation, ne doit jamais se retrouver caché derrière un « voir plus »
 * — ce sont exactement les deux cas où l'orchestrateur doit agir maintenant.
 *
 * Le reste — assigné, dans les temps, rien à trancher — remplit les places
 * déployées restantes jusqu'au seuil, puis se replie en lignes compactes
 * (nom · agent · horaire · état), chacune dépliable au clic.
 *
 * CONSÉQUENCE ASSUMÉE : une journée de 9 ménages dont 5 orphelins affiche 5
 * cartes pleines et aucune place libre pour les autres. C'est voulu. Le seuil
 * borne le confort de lecture, jamais la visibilité de ce qui presse : si la
 * journée part en vrille, l'écran doit le montrer, pas le lisser.
 *
 * @param {Array} runsTries       runs du jour, DÉJÀ triés par l'appelant.
 * @param {{ estOrphelin: Function, estEnRetard: Function, seuil?: number }} opts
 * @returns {{ deployees: Array, repliees: Array }}
 */
export function repartirCartes(runsTries, { estOrphelin, estEnRetard, seuil = SEUIL_CARTES_DEPLOYEES } = {}) {
  const list = Array.isArray(runsTries) ? runsTries : []
  const orph = typeof estOrphelin === 'function' ? estOrphelin : (r) => !r?.assigned_provider_email
  const retard = typeof estEnRetard === 'function' ? estEnRetard : () => false
  const plafond = Number.isFinite(seuil) && seuil > 0 ? seuil : SEUIL_CARTES_DEPLOYEES

  const deployees = []
  const repliees = []
  // 1er passage : les cartes à décision sont réservées, hors quota.
  const aDecision = new Set()
  for (const r of list) {
    if (orph(r) || retard(r)) aDecision.add(r)
  }
  // 2e passage : on parcourt dans l'ordre reçu pour ne pas casser le tri.
  let placesLibres = Math.max(0, plafond - aDecision.size)
  for (const r of list) {
    if (aDecision.has(r)) { deployees.push(r); continue }
    if (placesLibres > 0) { deployees.push(r); placesLibres -= 1; continue }
    repliees.push(r)
  }
  return { deployees, repliees }
}
